"use client";

import type { CreateProfileFormData } from "@/features/profile/tutor/schemas/createProfileSchema";

import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { type Control, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, toDate } from "date-fns";
import * as z from "zod";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { ErrorMessage } from "@hookform/error-message";

import { Button } from "@/components/ui/button";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CalendarWithYearSelect } from "@/components/ui/CalendarWithYear";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

interface CertificatesProps {
  control: Control<CreateProfileFormData>;
  setNavButtonsDisabled: Dispatch<SetStateAction<boolean>>;
}

export function Certificates({ control, setNavButtonsDisabled }: CertificatesProps) {
  const { t, i18n } = useTranslation(["forms", "common", "profile"]);
  const [currentStep, setCurrentStep] = useState(0); // 0 = list, 1 = add new
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const certificateSchema = z
    .object({
      name: z.string().min(3, t("forms:errors.certificateNameMin")).max(255),
      issuing_organization: z.string().min(3, t("forms:errors.issuingOrganizationMin")).max(255),
      issue_date: z.string().optional(),
      expiration_date: z.string().optional(),
      credential_id: z.string().max(255).optional(),
      credential_url: z
        .string()
        .url(t("forms:errors.invalidUrl"))
        .max(255)
        .optional()
        .or(z.literal("")),
      description: z.string().max(1000).optional(),
    })
    .refine(
      (data) => {
        if (data.issue_date && data.expiration_date) {
          return data.expiration_date >= data.issue_date;
        }

        return true;
      },
      {
        message: t("forms:errors.endDateAfterStartDate"),
        path: ["expiration_date"],
      },
    );

  type CertificateFormData = z.infer<typeof certificateSchema>;
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "certificates",
  });

  const form = useForm<CertificateFormData>({
    resolver: zodResolver(certificateSchema),
    defaultValues: {
      name: "",
      issuing_organization: "",
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    if (currentStep === 1 && fields.length > 0) {
      setNavButtonsDisabled(true);
    } else {
      setNavButtonsDisabled(false);
    }
  }, [currentStep, fields.length, setNavButtonsDisabled]);

  useEffect(() => {
    if (currentStep === 0) {
      form.reset({
        name: "",
        issuing_organization: "",
      });
    }
  }, [currentStep]);

  const handleAddCertificate = useCallback(
    (data: CertificateFormData) => {
      const result = certificateSchema.safeParse(data);

      if (result.success) {
        const formattedData = {
          ...result.data,
          issue_date: result.data.issue_date ?? undefined,
          expiration_date: result.data.expiration_date ?? undefined,
        };

        if (editingIndex !== null) {
          // Update existing certificate
          update(editingIndex, formattedData);
          setEditingIndex(null);
        } else {
          // Add new certificate
          append(formattedData);
        }

        form.reset();
        setCurrentStep(0);

        return true;
      } else {
        Object.keys(result.error.flatten().fieldErrors).forEach((fieldName) => {
          form.trigger(fieldName as keyof CertificateFormData);
        });

        return false;
      }
    },
    [certificateSchema, append, update, form, editingIndex],
  );

  const handleCancelCertificate = useCallback(() => {
    // Only reset form and go back to list view
    // Don't add empty fields to the array
    form.reset();
    setEditingIndex(null);
    setCurrentStep(0);
  }, [form]);

  const handleEditCertificate = useCallback(
    (index: number) => {
      const certificate = fields[index];

      setEditingIndex(index);
      form.reset(certificate);
      setCurrentStep(1);
    },
    [fields, form],
  );

  // Function to format date range for display
  const formatDateRange = useCallback(
    (issueDate?: Date | string, expirationDate?: Date | string) => {
      if (!issueDate) return "";

      const issue = format(new Date(issueDate), "MMM yyyy", {
        locale: getDateFnsLocale(i18n.language),
      });

      if (!expirationDate) {
        return `${issue}`;
      }

      const expiration = format(new Date(expirationDate), "MMM yyyy", {
        locale: getDateFnsLocale(i18n.language),
      });

      return `${issue} - ${expiration}`;
    },
    [i18n.language],
  );

  // Memoized animation variants
  const containerVariants = useMemo(
    () => ({
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          when: "beforeChildren",
          staggerChildren: 0.1,
          delayChildren: 0.2,
        },
      },
    }),
    [],
  );

  const stepVariants = useMemo(
    () => ({
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          type: "spring",
          stiffness: 300,
          damping: 30,
        },
      },
      exit: {
        opacity: 0,
        transition: {
          duration: 0.2,
        },
      },
    }),
    [],
  );

  const cardVariants = useMemo(
    () => ({
      hidden: { opacity: 0, scale: 0.95 },
      visible: (i: number) => ({
        opacity: 1,
        scale: 1,
        transition: {
          type: "spring",
          stiffness: 300,
          damping: 30,
          delay: i * 0.1,
        },
      }),
      exit: {
        opacity: 0,
        scale: 0.95,
        transition: {
          duration: 0.2,
          ease: "easeInOut",
        },
      },
    }),
    [],
  );

  // Step 0: Certificate List
  const CertificateListStep = () => (
    <motion.div
      animate="visible"
      className="flex h-full w-full flex-col"
      exit="exit"
      initial="hidden"
      variants={stepVariants}
    >
      {/* Compact Header */}
      <div className="mb-6 text-center">
        <div className="mb-3 flex items-center justify-center gap-2">
          <div className="bg-primary flex items-center justify-center rounded-full p-2">
            <Icon
              className="text-accent-foreground"
              height="1rem"
              icon="mdi:certificate-outline"
              width="1rem"
            />
          </div>
          <h2 className="text-base font-semibold sm:text-lg">
            {t("profile:certificatesSection.title")}
          </h2>
          <Badge className="bg-primary text-accent-foreground text-xs">
            {t("common:optional")}
          </Badge>
        </div>
        <p className="text-muted-foreground text-xs sm:text-sm">
          {t("profile:educationAndCertificates.certificatesDescription")}
        </p>
      </div>

      {/* Fixed Height List Area */}
      <div className="flex flex-col">
        <div className="flex flex-col">
          <ScrollArea className="h-[60vh] lg:h-[550px]">
            <div className="space-y-3">
              {fields.length > 0 && (
                <div className="flex justify-center pt-4">
                  <Button
                    className="bg-accent-foreground text-primary hover:bg-accent-foreground/90"
                    onClick={() => {
                      setEditingIndex(null);
                      form.reset();
                      setCurrentStep(1);
                    }}
                  >
                    <Icon className="mr-2" height="1rem" icon="mdi:plus" width="1rem" />
                    {t("forms:certificates.addCertificate")}
                  </Button>
                </div>
              )}
              <AnimatePresence mode="popLayout">
                {fields.map((field, index) => (
                  <motion.div
                    key={field.id}
                    layout
                    animate="visible"
                    custom={index}
                    exit="exit"
                    initial="hidden"
                    variants={cardVariants}
                  >
                    <div className="group bg-side-accent hover:bg-side-accent/50 relative overflow-hidden rounded-lg p-3 shadow-sm transition-all">
                      <div className="flex items-start justify-between">
                        <button
                          className="flex flex-1 cursor-pointer items-start space-x-3 border-none bg-transparent text-left outline-none"
                          onClick={() => handleEditCertificate(index)}
                        >
                          <div className="bg-accent-foreground flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full">
                            <Icon
                              className="text-primary"
                              height="1rem"
                              icon="mdi:certificate-outline"
                              width="1rem"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="text-accent-foreground truncate text-sm font-medium">
                              {field.name}
                            </h4>
                            {field.issuing_organization && (
                              <p className="text-muted-foreground truncate text-xs">
                                {field.issuing_organization}
                              </p>
                            )}
                            {(field.issue_date || field.expiration_date) && (
                              <p className="text-muted-foreground mt-1 text-xs">
                                {formatDateRange(field.issue_date, field.expiration_date)}
                              </p>
                            )}
                            {field.description && (
                              <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                                {field.description}
                              </p>
                            )}
                            {field.credential_id && (
                              <div className="mt-1 flex items-center text-xs">
                                <Icon
                                  className="text-muted-foreground mr-1.5"
                                  height="0.75rem"
                                  icon="mdi:identifier"
                                  width="0.75rem"
                                />
                                <span className="text-muted-foreground line-clamp-1">
                                  {field.credential_id}
                                </span>
                              </div>
                            )}
                            {field.credential_url && (
                              <div className="mt-1 flex items-center text-xs">
                                <Icon
                                  className="text-muted-foreground mr-1.5"
                                  height="0.75rem"
                                  icon="mdi:link-variant"
                                  width="0.75rem"
                                />
                                <a
                                  className="text-accent-foreground hover:text-accent-foreground/80 truncate underline"
                                  href={field.credential_url}
                                  rel="noopener noreferrer"
                                  target="_blank"
                                >
                                  {t("forms:certificates.viewCredential")}
                                </a>
                              </div>
                            )}
                          </div>
                        </button>
                        <Button
                          className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(index);
                          }}
                        >
                          <Icon height="0.875rem" icon="mdi:trash-outline" width="0.875rem" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {fields.length === 0 && (
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="flex h-[400px] items-center justify-center p-6"
                  initial={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="flex max-w-sm flex-col items-center text-center">
                    <motion.div
                      animate={{ scale: 1, rotate: 0 }}
                      className="bg-accent-foreground mb-6 flex items-center justify-center rounded-full p-6"
                      initial={{ scale: 0.8, rotate: -10 }}
                      transition={{
                        duration: 0.5,
                        delay: 0.2,
                        type: "spring",
                        stiffness: 200,
                        damping: 15,
                      }}
                    >
                      <Icon
                        className="text-primary"
                        height="3rem"
                        icon="mdi:certificate-outline"
                        width="3rem"
                      />
                    </motion.div>
                    <motion.h3
                      animate={{ opacity: 1 }}
                      className="text-accent-foreground mb-2 text-lg font-medium"
                      initial={{ opacity: 0 }}
                      transition={{ duration: 0.4, delay: 0.3 }}
                    >
                      {t("forms:certificates.noCertificates")}
                    </motion.h3>
                    <motion.p
                      animate={{ opacity: 1 }}
                      className="text-muted-foreground mb-6 max-w-lg text-sm leading-relaxed"
                      initial={{ opacity: 0 }}
                      transition={{ duration: 0.4, delay: 0.4 }}
                    >
                      {t("forms:certificates.noCertificatesSubtitle")}
                    </motion.p>
                    <motion.div
                      animate={{ opacity: 1, scale: 1 }}
                      initial={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.4, delay: 0.5 }}
                    >
                      <Button
                        className="bg-accent-foreground text-primary hover:bg-accent-foreground/90 gap-2"
                        onClick={() => {
                          setEditingIndex(null);
                          form.reset();
                          setCurrentStep(1);
                        }}
                      >
                        <Icon height="1rem" icon="mdi:plus" width="1rem" />
                        {t("forms:certificates.addCertificate")}
                      </Button>
                    </motion.div>
                  </div>
                </motion.div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Add Button */}
      </div>
    </motion.div>
  );

  // Step 1: Add New Certificate
  const AddCertificateStep = useMemo(() => {
    const AddCertificate = () => {
      return (
        <motion.div className="flex h-full w-full flex-col">
          {/* Compact Header */}
          <div className="mb-6 text-center">
            <div className="mb-3 flex items-center justify-center gap-2">
              <div className="bg-primary flex items-center justify-center rounded-full p-2">
                <Icon
                  className="text-accent-foreground"
                  height="1rem"
                  icon="mdi:certificate-outline"
                  width="1rem"
                />
              </div>
              <h2 className="text-lg font-semibold">
                {editingIndex !== null
                  ? t("forms:certificates.editCertificate")
                  : t("forms:certificates.addCertificate")}
              </h2>
              <Badge className="bg-primary text-accent-foreground text-xs">
                {t("common:optional")}
              </Badge>
            </div>
          </div>

          {/* Compact Form */}
          <div className="flex-1">
            <ScrollArea className="h-[60vh] lg:h-[700px]">
              <div className="space-y-5 p-1">
                {/* Row 1: Certificate & Timeline */}
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-side-accent mb-2 rounded-xl p-5 shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                    {/* Certificate Section */}
                    <div>
                      <div className="mb-4 flex items-center gap-3">
                        <div className="bg-accent-foreground flex h-8 w-8 items-center justify-center rounded-lg">
                          <Icon
                            className="text-primary"
                            height="1rem"
                            icon="mdi:certificate"
                            width="1rem"
                          />
                        </div>
                        <div>
                          <h3 className="text-accent-foreground text-sm font-semibold">
                            {t("forms:certificates.certificateDetails")}
                          </h3>
                        </div>
                      </div>

                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                              <Icon height="0.875rem" icon="mdi:certificate" width="0.875rem" />
                              {t("forms:certificates.certificateName")}
                            </div>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  className="h-10 pr-10 pl-3 transition-all focus-visible:ring-1 focus-visible:ring-offset-1"
                                  placeholder={t("forms:certificates.certificateNamePlaceholder")}
                                  {...field}
                                />
                                <div className="absolute inset-y-0 right-3 flex items-center">
                                  <Icon
                                    className={cn(
                                      "transition-colors",
                                      field.value.length >= 3
                                        ? "text-accent-foreground"
                                        : "text-muted-foreground",
                                    )}
                                    height="0.875rem"
                                    icon={
                                      field.value.length >= 3
                                        ? "mdi:check-circle"
                                        : "mdi:close-circle-outline"
                                    }
                                    width="0.875rem"
                                  />
                                </div>
                              </div>
                            </FormControl>
                            <p className="text-destructive mt-1 min-h-[1rem] text-xs">
                              <ErrorMessage errors={form.formState.errors} name="name" />
                            </p>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="issuing_organization"
                        render={({ field }) => (
                          <FormItem className="mt-4">
                            <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                              <Icon height="0.875rem" icon="mdi:domain" width="0.875rem" />
                              {t("forms:certificates.issuingOrganization")}
                            </div>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  className="h-10 pr-10 pl-3 transition-all focus-visible:ring-1 focus-visible:ring-offset-1"
                                  placeholder={t("forms:certificates.issuingOrgPlaceholder")}
                                  {...field}
                                />
                                <div className="absolute inset-y-0 right-3 flex items-center">
                                  <Icon
                                    className={cn(
                                      "transition-colors",
                                      field.value.length >= 3
                                        ? "text-accent-foreground"
                                        : "text-muted-foreground",
                                    )}
                                    height="0.875rem"
                                    icon={
                                      field.value.length >= 3
                                        ? "mdi:check-circle"
                                        : "mdi:close-circle-outline"
                                    }
                                    width="0.875rem"
                                  />
                                </div>
                              </div>
                            </FormControl>
                            <p className="text-destructive mt-1 min-h-[1rem] text-xs">
                              <ErrorMessage
                                errors={form.formState.errors}
                                name="issuing_organization"
                              />
                            </p>
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Timeline Section */}
                    <div>
                      <div className="mb-4 flex items-center gap-3">
                        <div className="bg-accent-foreground flex h-8 w-8 items-center justify-center rounded-lg">
                          <Icon
                            className="text-primary"
                            height="1rem"
                            icon="mdi:timeline-clock"
                            width="1rem"
                          />
                        </div>
                        <div>
                          <h3 className="text-accent-foreground text-sm font-semibold">
                            {t("forms:certificates.timeline")}
                          </h3>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="issue_date"
                          render={({ field }) => (
                            <FormItem>
                              <div className="text-muted-foreground mb-2 flex items-center gap-1 text-sm font-medium">
                                <Icon
                                  height="0.875rem"
                                  icon="mdi:calendar-start"
                                  width="0.875rem"
                                />
                                {t("forms:certificates.issueDate")}
                              </div>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <Button
                                      className={cn(
                                        "border-input bg-background h-10 w-full justify-start border px-3 text-left text-xs font-normal transition-all focus-visible:ring-1 focus-visible:ring-offset-1",
                                        !field.value && "text-muted-foreground",
                                      )}
                                      variant="outline"
                                    >
                                      <Icon
                                        className="mr-2"
                                        height="0.875rem"
                                        icon="mdi:calendar-outline"
                                        width="0.875rem"
                                      />
                                      {field.value ? (
                                        <span className="text-accent-foreground">
                                          {format(new Date(field.value), "MM/yyyy", {
                                            locale: getDateFnsLocale(i18n.language),
                                          })}
                                        </span>
                                      ) : (
                                        <span>{t("forms:certificates.selectDate")}</span>
                                      )}
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent align="start" className="w-auto p-0">
                                  <CalendarWithYearSelect
                                    initialFocus
                                    disabled={(date) => date > new Date()}
                                    fromYearSelect={new Date().getFullYear() - 49}
                                    language={i18n.language}
                                    mode="single"
                                    selected={field.value ? new Date(field.value) : undefined}
                                    weekStartsOn={1}
                                    onSelect={(date) =>
                                      field.onChange(format(date ?? new Date(), "yyyy-MM-dd"))
                                    }
                                  />
                                </PopoverContent>
                              </Popover>
                              <p className="text-destructive mt-1 min-h-[1rem] text-xs">
                                <ErrorMessage errors={form.formState.errors} name="issue_date" />
                              </p>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="expiration_date"
                          render={({ field }) => (
                            <FormItem>
                              <div className="text-muted-foreground mb-2 flex items-center gap-1 text-sm font-medium">
                                <Icon height="0.875rem" icon="mdi:calendar-end" width="0.875rem" />
                                {t("forms:certificates.expirationDate")}
                              </div>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <Button
                                      className={cn(
                                        "border-input bg-background h-10 w-full justify-start border px-3 text-left text-xs font-normal transition-all focus-visible:ring-1 focus-visible:ring-offset-1",
                                        !field.value && "text-muted-foreground",
                                        !form.watch("issue_date") && "opacity-50",
                                      )}
                                      disabled={!form.watch("issue_date")}
                                      variant="outline"
                                    >
                                      <Icon
                                        className="mr-2"
                                        height="0.875rem"
                                        icon="mdi:calendar-outline"
                                        width="0.875rem"
                                      />
                                      {field.value ? (
                                        <span className="text-accent-foreground">
                                          {format(new Date(field.value), "MM/yyyy", {
                                            locale: getDateFnsLocale(i18n.language),
                                          })}
                                        </span>
                                      ) : (
                                        <span>{t("forms:certificates.selectDate")}</span>
                                      )}
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent align="start" className="w-auto p-0">
                                  <CalendarWithYearSelect
                                    initialFocus
                                    disabled={(date) => {
                                      const startDate = form.watch("issue_date");

                                      return startDate ? date < toDate(startDate) : false;
                                    }}
                                    fromYearSelect={new Date().getFullYear() - 49}
                                    language={i18n.language}
                                    mode="single"
                                    selected={field.value ? new Date(field.value) : undefined}
                                    weekStartsOn={1}
                                    onSelect={(date) =>
                                      field.onChange(format(date ?? new Date(), "yyyy-MM-dd"))
                                    }
                                  />
                                </PopoverContent>
                              </Popover>
                              <p className="text-destructive mt-1 min-h-[1rem] text-xs">
                                <ErrorMessage
                                  errors={form.formState.errors}
                                  name="expiration_date"
                                />
                              </p>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Row 2: Credential Info & Description */}
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-side-accent rounded-xl p-5 shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                    {/* Credential Details Section */}
                    <div>
                      <div className="mb-4 flex items-center gap-3">
                        <div className="bg-accent-foreground flex h-8 w-8 items-center justify-center rounded-lg">
                          <Icon
                            className="text-primary"
                            height="1rem"
                            icon="mdi:file-document"
                            width="1rem"
                          />
                        </div>
                        <div>
                          <h3 className="text-accent-foreground text-sm font-semibold">
                            {t("forms:certificates.additionalInfo")}
                          </h3>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="credential_id"
                          render={({ field }) => (
                            <FormItem>
                              <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                                <Icon height="0.875rem" icon="mdi:identifier" width="0.875rem" />
                                {t("forms:certificates.credentialId")}
                              </div>
                              <FormControl>
                                <Input
                                  className="h-10 pr-3 pl-3 transition-all focus-visible:ring-1 focus-visible:ring-offset-1"
                                  placeholder={t("forms:certificates.credentialIdPlaceholder")}
                                  {...field}
                                />
                              </FormControl>
                              <p className="text-destructive mt-1 min-h-[1rem] text-xs">
                                <ErrorMessage errors={form.formState.errors} name="credential_id" />
                              </p>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="credential_url"
                          render={({ field }) => (
                            <FormItem>
                              <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                                <Icon height="0.875rem" icon="mdi:link-variant" width="0.875rem" />
                                {t("forms:certificates.credentialUrl")}
                              </div>
                              <FormControl>
                                <Input
                                  className="h-10 pr-3 pl-3 transition-all focus-visible:ring-1 focus-visible:ring-offset-1"
                                  placeholder={t("forms:certificates.credentialUrlPlaceholder")}
                                  type="url"
                                  {...field}
                                />
                              </FormControl>
                              <p className="text-destructive mt-1 min-h-[1rem] text-xs">
                                <ErrorMessage
                                  errors={form.formState.errors}
                                  name="credential_url"
                                />
                              </p>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    {/* Description Section */}
                    <div>
                      <div className="mb-4 flex items-center gap-3">
                        <div className="bg-accent-foreground flex h-8 w-8 items-center justify-center rounded-lg">
                          <Icon
                            className="text-primary"
                            height="1rem"
                            icon="mdi:text-box-outline"
                            width="1rem"
                          />
                        </div>
                        <div>
                          <h3 className="text-accent-foreground text-sm font-semibold">
                            {t("forms:certificates.description")}
                          </h3>
                        </div>
                      </div>

                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                              <Icon height="0.875rem" icon="mdi:text" width="0.875rem" />
                              {t("forms:certificates.description")}
                            </div>
                            <FormControl>
                              <div className="relative">
                                <ScrollArea className="border-input bg-background ring-offset-background focus-within:ring-ring h-[147px] w-full rounded-xl">
                                  <Textarea
                                    className="[field-sizing:content] min-h-[130px] resize-none rounded-xl border-none! p-4 text-base break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                                    placeholder={t("forms:education.descriptionPlaceholder")}
                                    {...field}
                                  />
                                </ScrollArea>
                                <div className="text-muted-foreground absolute right-3 bottom-2 text-xs">
                                  {field.value?.length || 0}/1000
                                </div>
                              </div>
                            </FormControl>
                            <p className="text-destructive mt-1 min-h-[1rem] text-xs">
                              <ErrorMessage errors={form.formState.errors} name="description" />
                            </p>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-between pt-5"
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Button
                      className="h-11 px-6"
                      size="lg"
                      variant="outline"
                      onClick={handleCancelCertificate}
                    >
                      <Icon className="mr-2" height="1rem" icon="mdi:arrow-left" width="1rem" />
                      {t("common:cancel")}
                    </Button>
                    <Button
                      className="bg-accent-foreground text-primary hover:bg-accent-foreground/90 ms-auto h-11 px-8"
                      size="lg"
                      onClick={() => handleAddCertificate(form.getValues())}
                    >
                      <Icon className="mr-2" height="1rem" icon="mdi:check-circle" width="1rem" />
                      {editingIndex !== null ? t("common:update") : t("common:save")}
                    </Button>
                  </motion.div>
                </motion.div>
              </div>
            </ScrollArea>
          </div>
        </motion.div>
      );
    };

    AddCertificate.displayName = "AddCertificate";

    return AddCertificate;
  }, [fields, i18n.language, stepVariants, editingIndex, handleCancelCertificate]);

  return (
    <motion.div
      animate="visible"
      className="flex h-full w-full flex-col items-center justify-center p-4 text-start md:p-6"
      initial="hidden"
      variants={containerVariants}
    >
      <div className={"my-auto w-full"}>
        <AnimatePresence mode={"wait"}>
          {currentStep === 0 ? (
            <CertificateListStep key="certificate-list" />
          ) : (
            <AddCertificateStep key="certificate-add" />
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
